import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Shield, 
  FileText, 
  Users, 
  CreditCard, 
  TrendingUp, 
  AlertCircle,
  CheckCircle,
  Clock,
  Bot,
  Sparkles,
  ArrowRight,
  Calendar,
  DollarSign,
  Activity,
  MapPin,
  Calculator,
  Upload,
  Loader2,
  Plus,
  Edit,
  Camera,
  Scan
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useData } from '../contexts/DataContext';
import AIChatAssistant from './AIChatAssistant';
import ManualBenefitsInput from './Benefits/ManualBenefitsInput';
import MilitaryDocumentUpload from './Import/MilitaryDocumentUpload';
import DocumentManager from './Documents/DocumentManager';
import DocumentsCounter from './ui/DocumentsCounter';
import MAILogo from './ui/MAILogo';

const Dashboard = ({ chatMinimized = true, onToggleChat }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [showManualInput, setShowManualInput] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const location = useLocation();
  
  // Handle URL parameters for tab switching
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
      // Clear the URL parameter after setting the tab
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search]);
  
  const { 
    user, 
    benefits, 
    documents, 
    notifications, 
    aiInsights, 
    loading, 
    error,
    refreshInsights,
    setBenefits
  } = useData();

  if (loading) {
    return (
      <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-blue-600">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-lg font-medium">Loading your military benefits dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'expired': return 'text-red-600 bg-red-100';
      case 'processing': return 'text-blue-600 bg-blue-100';
      case 'verified': return 'text-green-600 bg-green-100';
      case 'available': return 'text-blue-600 bg-blue-100';
      case 'eligible': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      MapPin,
      CreditCard,
      Users,
      FileText,
      Calculator,
      Upload,
      Shield
    };
    return icons[iconName] || FileText;
  };

  const quickActions = [
    {
      id: 1,
      title: "eCorrespondence & Proof of Coverage",
      description: "Access official correspondence and coverage documents",
      icon: "FileText",
      aiEnabled: true,
      aiFeature: "AI analyzes correspondence for important deadlines and actions",
      url: "https://milconnect.dmdc.osd.mil/milconnect/faces/correspondence"
    },
    {
      id: 2,
      title: "Defense Personal Records (DPRIS)",
      description: "View and manage your defense personnel records",
      icon: "Shield",
      aiEnabled: true,
      aiFeature: "Smart organization and analysis of personnel records",
      url: "https://milconnect.dmdc.osd.mil/milconnect/faces/dpris"
    },
    {
      id: 3,
      title: "Beneficiary Web Enrollment (BWE)",
      description: "Enroll and manage beneficiaries for your benefits",
      icon: "Users",
      aiEnabled: true,
      aiFeature: "Optimal beneficiary recommendations based on family status",
      url: "https://milconnect.dmdc.osd.mil/milconnect/faces/bwe"
    },
    {
      id: 4,
      title: "Transfer Education Benefits (TEB)",
      description: "Transfer your GI Bill benefits to family members",
      icon: "Calculator",
      aiEnabled: true,
      aiFeature: "Calculates optimal transfer amounts and timing",
      url: "https://milconnect.dmdc.osd.mil/milconnect/faces/teb"
    },
    {
      id: 5,
      title: "Life Insurance (SGLI/FSGLI)",
      description: "Manage SGLI and Family SGLI enrollment",
      icon: "CreditCard",
      aiEnabled: true,
      aiFeature: "Coverage optimization based on family and financial status",
      url: "https://milconnect.dmdc.osd.mil/milconnect/faces/soes"
    },
    {
      id: 6,
      title: "DoD Transition Assistance (DoDTAP)",
      description: "Access transition assistance program resources",
      icon: "MapPin",
      aiEnabled: true,
      aiFeature: "Personalized transition planning and resource recommendations",
      url: "https://milconnect.dmdc.osd.mil/milconnect/faces/dodtap"
    }
  ];

  const handleBenefitAdded = (newBenefit) => {
    setBenefits(prev => [...prev, newBenefit]);
  };

  const handleDocumentDataExtracted = (extractedData) => {
    // Add extracted benefits to user's benefits
    if (extractedData.benefits && extractedData.benefits.length > 0) {
      setBenefits(prev => [...prev, ...extractedData.benefits]);
    }
    
    // Close modal
    setShowDocumentUpload(false);
    
    // Show success message or notification
    console.log('Document data extracted:', extractedData);
  };

  const activeBenefits = benefits.filter(b => b.status === 'Active').length;
  const processedDocs = documents.filter(d => d.ai_processed).length;
  const unreadNotifications = notifications.filter(n => !n.read).length;

  const quickStats = [
    {
      title: "Active Benefits",
      value: activeBenefits,
      total: benefits.length,
      icon: Shield,
      color: "text-green-600",
      bgColor: "bg-green-100"
    },
    {
      title: "Documents Processed",
      value: processedDocs,
      total: documents.length,
      icon: FileText,
      color: "text-blue-600",
      bgColor: "bg-blue-100"
    },
    {
      title: "AI Recommendations",
      value: aiInsights?.recommendations?.length || 0,
      total: null,
      icon: Sparkles,
      color: "text-purple-600",
      bgColor: "bg-purple-100"
    },
    {
      title: "Unread Notifications",
      value: unreadNotifications,
      total: null,
      icon: Clock,
      color: "text-orange-600",
      bgColor: "bg-orange-100"
    }
  ];

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Welcome back, {user?.rank} {user?.name?.split(' ')[1] || 'Soldier'}
            </h1>
            <p className="text-blue-100 text-lg">
              Your AI-powered military benefits dashboard • Last updated: {new Date().toLocaleDateString()}
            </p>
            <div className="flex items-center gap-4 mt-4">
              <Badge className="bg-green-500/20 text-green-200 border-green-400">
                <CheckCircle className="w-3 h-3 mr-1" />
                All systems operational
              </Badge>
              <Badge className="bg-blue-500/20 text-blue-200 border-blue-400">
                <MAILogo className="w-3 h-3 mr-1" />
                AI assistant ready
              </Badge>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
              <Shield className="w-12 h-12 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickStats.map((stat) => (
          <Card key={stat.title} className="hover:shadow-lg transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
                    {stat.total && (
                      <span className="text-sm text-gray-500">/ {stat.total}</span>
                    )}
                  </div>
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Documents Counter */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <DocumentsCounter />
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 bg-white/80 backdrop-blur-sm">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="benefits">Benefits</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Quick Actions */}
          <Card className="border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {quickActions.slice(0, 6).map((action) => {
                  const IconComponent = getIconComponent(action.icon);
                  return (
                    <Button 
                      key={action.id}
                      variant="outline" 
                      className="h-auto p-4 text-left justify-start hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 group"
                      onClick={() => window.open(action.url, '_blank')}
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                          <IconComponent className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-gray-900">{action.title}</h3>
                            {action.aiEnabled && (
                              <Sparkles className="w-3 h-3 text-emerald-500" />
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{action.description}</p>
                          {action.aiEnabled && (
                            <p className="text-xs text-emerald-600 font-medium">{action.aiFeature}</p>
                          )}
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                      </div>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {notifications.slice(0, 3).map((notification) => (
                  <div key={notification.id} className={`flex items-start gap-3 p-3 rounded-lg border ${
                    notification.type === 'ai' ? 'bg-green-50 border-green-200' :
                    notification.type === 'reminder' ? 'bg-orange-50 border-orange-200' :
                    'bg-blue-50 border-blue-200'
                  }`}>
                    {notification.type === 'ai' && <MAILogo className="w-5 h-5 mt-0.5" />}
                    {notification.type === 'reminder' && <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />}
                    {notification.type === 'update' && <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />}
                    <div>
                      <p className={`font-medium ${
                        notification.type === 'ai' ? 'text-green-900' :
                        notification.type === 'reminder' ? 'text-orange-900' :
                        'text-blue-900'
                      }`}>{notification.title}</p>
                      <p className={`text-sm ${
                        notification.type === 'ai' ? 'text-green-700' :
                        notification.type === 'reminder' ? 'text-orange-700' :
                        'text-blue-700'
                      }`}>{notification.message} • {new Date(notification.timestamp).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="benefits" className="space-y-6">
          {/* Benefits Header with Action Buttons */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Your Military Benefits</h2>
              <p className="text-gray-600">Manage and track your military benefits and entitlements</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDocumentUpload(true)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Scan className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
              <Button
                onClick={() => setShowManualInput(true)}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Manually
              </Button>
            </div>
          </div>

          {/* AI Document Upload Modal */}
          {showDocumentUpload && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <MilitaryDocumentUpload
                  userId={user?.id}
                  onDataExtracted={handleDocumentDataExtracted}
                  onClose={() => setShowDocumentUpload(false)}
                />
              </div>
            </div>
          )}

          {/* Manual Benefits Input Modal */}
          {showManualInput && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <ManualBenefitsInput
                  userId={user?.id}
                  onBenefitAdded={handleBenefitAdded}
                  onClose={() => setShowManualInput(false)}
                />
              </div>
            </div>
          )}

          {/* AI Processing Intro */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border border-green-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Scan className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  AI Document Processing Available
                </h3>
                <p className="text-gray-700 mb-3">
                  Upload your military documents (LES, benefits statements, orders, VA letters) and let Claude AI automatically extract and organize your military data.
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Supports PDF, images, text</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>AI extracts benefits data</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Manual verification step</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits List */}
          <div className="grid gap-6">
            {benefits.map((benefit) => (
              <Card key={benefit.id} className="border-0 bg-white/80 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{benefit.name}</h3>
                      <Badge className={getStatusColor(benefit.status)}>
                        {benefit.status}
                      </Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Type</p>
                      <p className="font-medium capitalize text-gray-900">{benefit.type}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {benefit.coverage && (
                      <div>
                        <p className="text-sm text-gray-600">Coverage</p>
                        <p className="font-medium text-gray-900">{benefit.coverage}</p>
                      </div>
                    )}
                    {benefit.remaining_months && (
                      <div>
                        <p className="text-sm text-gray-600">Remaining</p>
                        <p className="font-medium text-gray-900">{benefit.remaining_months} months</p>
                      </div>
                    )}
                    {benefit.premium && (
                      <div>
                        <p className="text-sm text-gray-600">Premium</p>
                        <p className="font-medium text-gray-900">{benefit.premium}</p>
                      </div>
                    )}
                    {benefit.expiry_date && (
                      <div>
                        <p className="text-sm text-gray-600">Expires</p>
                        <p className="font-medium text-gray-900">{benefit.expiry_date}</p>
                      </div>
                    )}
                  </div>

                  {benefit.ai_recommendation && (
                    <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-lg p-4 border border-blue-200">
                      <div className="flex items-start gap-2">
                        <MAILogo className="w-4 h-4 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900 mb-1">AI Recommendation</p>
                          <p className="text-sm text-gray-700">{benefit.ai_recommendation}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="documents" className="space-y-6">
          <DocumentManager userId={user?.id} />
        </TabsContent>

        <TabsContent value="ai-insights" className="space-y-6">
          {aiInsights ? (
            <>
              {/* Benefit Optimization Score */}
              <Card className="border-0 bg-gradient-to-r from-blue-600 to-emerald-600 text-white">
                <CardContent className="p-8">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold mb-2">Benefit Optimization Score</h2>
                    <div className="text-5xl font-bold mb-4">{aiInsights.benefit_optimization_score}/100</div>
                    <p className="text-blue-100">Your benefits are well-optimized with room for improvement</p>
                    <Button 
                      onClick={refreshInsights}
                      className="mt-4 bg-white/20 hover:bg-white/30 text-white border-white/30"
                    >
                      Refresh Analysis
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* AI Recommendations */}
              <Card className="border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-600" />
                    Smart Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {aiInsights.recommendations.map((rec, index) => (
                      <div key={`rec-${index}-${rec.slice(0, 20)}`} className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                        <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs font-bold">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-900">{rec}</p>
                        </div>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                          Apply
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Deadlines */}
              <Card className="border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-orange-600" />
                    Upcoming Deadlines
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {aiInsights.upcoming_deadlines.map((deadline) => (
                      <div key={deadline.task} className={`flex items-center justify-between p-3 rounded-lg border ${
                        deadline.priority === 'high' ? 'bg-red-50 border-red-200' :
                        deadline.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                        'bg-blue-50 border-blue-200'
                      }`}>
                        <div>
                          <p className="font-medium text-gray-900">{deadline.task}</p>
                          <p className="text-sm text-gray-600">Due: {deadline.dueDate}</p>
                        </div>
                        <Badge variant={deadline.priority === 'high' ? 'destructive' : 'secondary'}>
                          {deadline.priority}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-0 bg-white/80 backdrop-blur-sm">
              <CardContent className="p-8 text-center">
                <MAILogo className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">AI Insights Loading</h3>
                <p className="text-gray-600 mb-4">Analyzing your benefits and generating personalized insights...</p>
                <Button onClick={refreshInsights} className="bg-blue-600 hover:bg-blue-700">
                  Generate Insights
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* AI Chat Assistant */}
      <AIChatAssistant 
        isMinimized={chatMinimized}
        onToggleMinimize={onToggleChat}
      />
    </div>
  );
};

export default Dashboard;